import React, { useEffect, useState } from "react";
import GridLayout from "react-grid-layout";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import { useAxiosContext } from "../../context/AxiosContext";
import { useFormContext } from "../../context/FormContext";
import useElementDimensions from "../../hooks/useElementDimensions";
import { getFormFieldConfig, getSubFormFieldConfig } from "../../utils/Config/getDefaultConfig";
import { getFieldIcon } from "../../utils/getFieldIcon";
import FieldTag from "./FieldTags/FieldTag";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import { generateObjectId } from "../../utils/objectIdUtils";
import { toastError } from "../../utils/toastUtils";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance, { handleApiError } from "../../services/axiosService";

const FormGridLayout = ({
  setEditFieldId,
  dragFromOutside,
  setDragFromOutside,
  editFieldId,
  fieldId,
  activeMode,
  from
}) => {
  const { renderFieldsData, renderFieldsArray, setRenderFieldsArray, selectedSectionData, formSectionsData } =
    useFormContext();
  const [{ width: elementWidth }, elementRef] = useElementDimensions();
  const [formGridArray, setFormGridArray] = useState([]);
  const [fieldsRearrangeModal, setFieldsRearrangeModal] = useState(false);
  const [subForms, setSubForms] = useState([]);

  // --- NEW STATE: SEARCH QUERY ---
  const [searchQuery, setSearchQuery] = useState("");

  const settingRenderArrayFromSectionData = () => {
    return renderFieldsArray.map((obj, i) => {
      const field = renderFieldsData.current[obj.i];

      const isNested = field.type === "container" || field.type === "recordset" || field.type === "recordset-list";

      const fieldLayout = {
        w: 12,
        h: isNested ? field.components.length + 1 : 1,
        x: 0,
        y: i,
        i: field.id
      };

      const existingItem = renderFieldsArray.find((item) => item.i === field.id);
      if (existingItem) {
        fieldLayout.y = existingItem.y;
      }

      return fieldLayout;
    });
  };
  const navigate = useNavigate();
  const { orgId, appId } = useParams();

  const handleNavigateToSubform = (element) => {
    if (element.type === "global-data") {
      const subformId = findSubformIdByFieldId(subForms, element?.components?.[0]?.id);
      if (!subformId) return toastError("Subform not found");
      navigate(`/organization/${orgId}/app/${appId}/smform/subform/${subformId}`);
      return;
    }
    if (!element.sub_form) return toastError("Subform not found");
    navigate(`/organization/${orgId}/app/${appId}/smform/subform/${element.sub_form}`);
  };

  const findSubformIdByFieldId = (subforms, fieldId) => {
    for (const subform of subforms) {
      if (subform?.config?.fields) {
        for (const field of subform.config.fields) {
          if (field.id === fieldId) {
            return subform.id;
          }
        }
      }
    }
    return null;
  };

  const onDrop = (elemParams, item, e) => {
    // --- 1. BLOCK DROP IF SEARCHING ---
    if (searchQuery) {
      toastError("Please clear search before adding new fields");
      return;
    }

    if (!item || !item.i) return;
    let type = e.dataTransfer.getData("type");
    if (type.length === 0) return;
    setDragFromOutside(false);
    let data = {};
    if (from === "subform") {
      data = getSubFormFieldConfig(type);
    } else {
      data = getFormFieldConfig(type);
    }
    if (fieldId && (type === "recordset" || type === "recordset-list" || type === "container")) {
      toastError("Can't drop group field inside");
      return;
    }

    const obj = {
      ...data,
      id: item.i,
      key: data.type + "_" + (Math.floor(Math.random() * 900000) + 100000)
    };
    renderFieldsData.current[item.i] = obj;

    if (fieldId) {
      const tempRenderArray = JSON.parse(JSON.stringify(renderFieldsData.current));
      tempRenderArray[fieldId].components.push(obj);
      renderFieldsData.current = tempRenderArray;
      changeNestedLayout(elemParams);
      const newOuter = settingRenderArrayFromSectionData();
      setRenderFieldsArray(newOuter);
    } else {
      setRenderFieldsArray(elemParams);
    }
    handleDesignLayout("add", item);
    setFormGridArray(elemParams);
  };

  const handleDesignLayout = (type, item) => {
    if (from !== "form" && from !== "subform") return;
    const layoutKey = fieldId ? fieldId : "outer_layout";

    const updateLayout = (layout) => {
      if (!formSectionsData.current[selectedSectionData][layout]) {
        formSectionsData.current[selectedSectionData][layout] = {};
      }

      const currentLayout = formSectionsData.current[selectedSectionData][layout][layoutKey] || [];
      let newArray;

      if (type === "add") {
        let newItem;
        if (formSectionsData.current[selectedSectionData]?.is_section_tab_layout) {
          newItem = {
            ...item,
            tab_id: formSectionsData.current[selectedSectionData]?.section_tabs?.[0]?.id
          };
        } else {
          newItem = { ...item };
        }
        newArray = [newItem, ...currentLayout];
      } else if (type === "delete") {
        newArray = currentLayout.filter((f) => f.i !== item);
      } else {
        return;
      }

      formSectionsData.current[selectedSectionData][layout][layoutKey] = newArray;
    };

    updateLayout("desktop_layout");
    updateLayout("mobile_layout");
    updateLayout("tablet_layout");
  };

  const changeNestedLayout = (list) => {
    const tempRenderArray = JSON.parse(JSON.stringify(renderFieldsData.current));
    const arrayCopy = [...list];
    arrayCopy.sort((a, b) => a.y - b.y);
    tempRenderArray[fieldId].components = arrayCopy.map((itemId) => {
      const foundItem = tempRenderArray[fieldId].components.find((item) => item.id === itemId.i);
      return foundItem;
    });

    renderFieldsData.current = tempRenderArray;
  };

  const handleLayoutChange = (list) => {
    // --- SAFETY CHECK: STOP SAVING IF SEARCHING ---
    if (searchQuery) return;
    if (dragFromOutside) return;

    if (activeMode === "desktop") {
      const newSectionData = JSON.parse(JSON.stringify(formSectionsData.current));
      const layoutKey = fieldId ? fieldId : "outer_layout";
      newSectionData[selectedSectionData].desktop_layout[layoutKey] = list;
      formSectionsData.current = newSectionData;
      setRenderFieldsArray([...renderFieldsArray]);
    } else if (activeMode === "mobile") {
      const newSectionData = JSON.parse(JSON.stringify(formSectionsData.current));
      const layoutKey = fieldId ? fieldId : "outer_layout";
      newSectionData[selectedSectionData].mobile_layout[layoutKey] = list;
      formSectionsData.current = newSectionData;
      setRenderFieldsArray([...renderFieldsArray]);
    } else if (activeMode === "tablet") {
      const newSectionData = JSON.parse(JSON.stringify(formSectionsData.current));
      const layoutKey = fieldId ? fieldId : "outer_layout";
      newSectionData[selectedSectionData].tablet_layout[layoutKey] = list;
      formSectionsData.current = newSectionData;
      setRenderFieldsArray([...renderFieldsArray]);
    } else {
      if (fieldId) {
        changeNestedLayout(list);
        setRenderFieldsArray([...renderFieldsArray]);
      } else {
        setRenderFieldsArray(list);
      }
    }
    setFormGridArray(list);
  };

  const editFormField = (e, id) => {
    e.stopPropagation();
    let parent = null;
    if (fieldId) parent = fieldId;
    setEditFieldId({
      parent: parent,
      id: id
    });
  };

  const copyFormField = (e, y, id) => {
    e.stopPropagation();
    const obj = fieldId ? renderFieldsData.current[fieldId].components[y] : renderFieldsData.current[id];
    const tempObj = JSON.parse(JSON.stringify(obj));
    let rendomKey = tempObj.type + "_" + (Math.floor(Math.random() * 900000) + 100000);
    const copyObj = { ...tempObj, id: generateObjectId(), key: rendomKey };

    if (copyObj.type === "container" || copyObj.type === "recordset" || copyObj.type === "recordset-list") {
      const newArray = copyObj.components.map((field) => {
        return {
          ...field,
          id: generateObjectId(),
          key: field.type + "_" + (Math.floor(Math.random() * 900000) + 100000)
        };
      });
      copyObj.components = newArray;
    }
    renderFieldsData.current[copyObj.id] = copyObj;
    const renderField = {
      w: 12,
      h:
        copyObj.type === "container" || copyObj.type === "recordset" || copyObj.type === "recordset-list"
          ? copyObj.components.length + 1
          : 1,
      x: 0,
      y: y + 1,
      i: copyObj.id
    };
    if (fieldId) {
      const tempData = JSON.parse(JSON.stringify(renderFieldsData.current));

      tempData[fieldId].components.splice(y, 0, copyObj);
      renderFieldsData.current = tempData;

      changeNestedLayout([renderField, ...formGridArray]);
      const newOuter = settingRenderArrayFromSectionData();
      setRenderFieldsArray(newOuter);
    } else {
      setRenderFieldsArray([renderField, ...renderFieldsArray]);
    }
    handleDesignLayout("add", renderField);
    setFormGridArray([renderField, ...formGridArray]);
  };

  const deleteFormField = (e, id) => {
    e.stopPropagation();
    const newArray = renderFieldsArray.filter((field) => field.i !== id);
    delete renderFieldsData.current[id];
    if (!fieldId) {
      setRenderFieldsArray(newArray);
    } else {
      const copyRederFieldData = JSON.parse(JSON.stringify(renderFieldsData.current));
      const newComponent = copyRederFieldData[fieldId].components.filter((field) => field.id !== id);
      copyRederFieldData[fieldId].components = newComponent;
      renderFieldsData.current = copyRederFieldData;
      changeNestedLayout(newArray);
      const newOuter = settingRenderArrayFromSectionData();
      setRenderFieldsArray(newOuter);
    }
    handleDesignLayout("delete", id);
    setFormGridArray(formGridArray.filter((field) => field.i !== id));
  };

  const makeFunction = () => {
    const newRenderArray = renderFieldsData.current[fieldId].components.map((field, i) => {
      return {
        w: 12,
        h:
          field.type === "container" || field.type === "recordset" || field.type === "recordset-list"
            ? field.components.length + 1
            : 1,
        x: 0,
        y: i,
        i: field.id
      };
    });
    return newRenderArray;
  };

  const reArrangeAndEditGroupField = (id) => {
    setFieldsRearrangeModal(id);
  };

  function filterMainArray(mainArray, actualArray) {
    const actualKeys = new Set(actualArray.map((obj) => obj.i));
    const filteredMainArray = mainArray.filter((obj) => actualKeys.has(obj.i));
    return filteredMainArray;
  }

  const getIconType = (id) => {
    if (fieldId) {
      const currentField = renderFieldsData.current[fieldId].components.find((f) => f.id === id);
      if (currentField.type === "global-data") {
        return currentField.global_field?.field_type || currentField.type;
      }
      return currentField.type;
    } else {
      const currentField = renderFieldsData.current[id];
      if (currentField.type === "global-data") {
        return currentField.global_field?.field_type || currentField.type;
      }
      return currentField.type;
    }
  };

  useEffect(() => {
    // fetch subforms
    const getSubForms = async () => {
      try {
        const { data } = await axiosInstance.get(`/subform`);
        setSubForms(data);
      } catch (error) {
        handleApiError(error);
      }
    };

    getSubForms();
  }, []);

  useEffect(() => {
    let renderarray = [];
    if (activeMode === "desktop") {
      if (fieldId) {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].desktop_layout[fieldId] || [],
          makeFunction()
        );
      } else {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].desktop_layout["outer_layout"] || [],
          renderFieldsArray
        );
      }
    } else if (activeMode === "mobile") {
      if (fieldId) {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].mobile_layout[fieldId] || [],
          makeFunction()
        );
      } else {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].mobile_layout["outer_layout"] || [],
          renderFieldsArray
        );
      }
    } else if (activeMode === "tablet") {
      if (fieldId) {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].tablet_layout[fieldId] || [],
          makeFunction()
        );
      } else {
        renderarray = filterMainArray(
          formSectionsData.current[selectedSectionData].tablet_layout["outer_layout"] || [],
          renderFieldsArray
        );
      }
    } else {
      renderarray = fieldId ? makeFunction() : renderFieldsArray;
    }

    // --- 2. FILTER BY SEARCH QUERY (Label, Type, or ID) ---
    // Only apply filter if there's no fieldId (nested items don't have search)
    if (!fieldId && searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      renderarray = renderarray.filter((item) => {
        // We know we are at root level because of the check above
        const fieldData = renderFieldsData.current[item.i];
        const label = fieldData?.label || "";
        const type = fieldData?.type || "";
        const id = fieldData?.id || item.i || "";

        // Match Label, Type, or ID
        return (
          label.toLowerCase().includes(query) || type.toLowerCase().includes(query) || id.toLowerCase().includes(query)
        );
      });
    }

    setFormGridArray(renderarray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, renderFieldsArray, searchQuery]);
  console.log("search", searchQuery, activeMode);

  return (
    <>
      {/* --- SEARCH BAR (Hidden if nested) --- */}
      {!fieldId && (
        // <div style={{ marginBottom: "8px", padding: "0 2px" }}>
        <Input
          type="text"
          placeholder="Search fields by label, type, or id..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          containerStyle={{ width: "200px" }}
        />
        // </div>
      )}

      {formGridArray.length === 0 && (
        <div className="add_form_container">
          <span>{searchQuery ? "No fields match your search" : "Drag and drop fields"}</span>
        </div>
      )}
      <div
        id="droppableContainerWidth"
        ref={elementRef}
        className={`sub_formrenderer_section ${from === "form" || from === "subform" ? "mt_4" : "mt_0"}`}
      >
        <GridLayout
          cols={12}
          rowHeight={120}
          width={elementWidth}
          layout={formGridArray}
          onDrop={(elemParams, item, e) => onDrop(elemParams, item, e)}
          compactType={"vertical"}
          style={{
            minHeight: "100px",
            border: fieldId && "2px dashed #e4e7ec",
            height: "100%",
            // overflow: "hidden scroll",
            paddingTop: "8px"
          }}
          // --- 3. DISABLE DROP IF SEARCHING ---
          isDroppable={!searchQuery}
          droppingItem={{ i: generateObjectId(), h: 1, w: 12 }}
          useCSSTransforms={true}
          // --- DISABLE DRAG/RESIZE IF SEARCHING ---
          isDraggable={!searchQuery}
          isResizable={!searchQuery && (activeMode === "desktop" || activeMode === "mobile" || activeMode === "tablet")}
          onDragStart={(layout, oldItem, newItem, _, e) => e.stopPropagation()}
          onLayoutChange={handleLayoutChange}
        >
          {formGridArray.length > 0 ? (
            formGridArray.map((field, i) => (
              <div key={field.i} className={`single_field_render ${editFieldId?.id === field.i ? "active" : ""}`}>
                {activeMode !== "mobile" && activeMode !== "desktop" && activeMode !== "tablet" && (
                  <div className={`form-buttons ${editFieldId?.id ? "edit_field" : ""}`}>
                    {(renderFieldsData.current[field.i]?.type === "recordset-list" ||
                      renderFieldsData.current[field.i]?.type === "grid" ||
                      renderFieldsData.current[field.i]?.type === "rich-grid" ||
                      (renderFieldsData.current[field.i]?.type === "global-data" &&
                        (renderFieldsData.current[field.i]?.global_field?.field_type === "recordset-list" ||
                          renderFieldsData.current[field.i]?.global_field?.field_type === "grid" ||
                          renderFieldsData.current[field.i]?.global_field?.field_type === "rich-grid"))) && (
                      <ButtonActionIcon type="list_alt" size="sm" onClick={() => reArrangeAndEditGroupField(field.i)} />
                    )}
                    {(renderFieldsData.current[field.i]?.type === "rich-grid" ||
                      (renderFieldsData.current[field.i]?.type === "global-data" &&
                        renderFieldsData.current[field.i]?.global_field?.field_type === "rich-grid")) && (
                      <ButtonActionIcon
                        type="arrow_outward"
                        size="sm"
                        title="navigate to subform"
                        onClick={() => handleNavigateToSubform(renderFieldsData.current[field.i])}
                      />
                    )}
                    <ButtonActionIcon type="edit" size="sm" onClick={(e) => editFormField(e, field.i)} />
                    <ButtonActionIcon
                      type="content_copy"
                      size="sm"
                      onClick={(e) => copyFormField(e, field.y, field.i, i)}
                    />
                    <ButtonActionIcon type="delete" size="sm" onClick={(e) => deleteFormField(e, field.i, i)} />
                  </div>
                )}
                <MaterialIcon
                  name={getFieldIcon(getIconType(field.i))}
                  style={{
                    color: (() => {
                      const type = fieldId
                        ? renderFieldsData.current[fieldId].components.find((f) => f.id === field.i).type
                        : renderFieldsData.current[field.i].type;
                      if (type === "lookup") return "red";
                      if (type === "global-data") return "#61cc1e";
                      return "#667085";
                    })(),
                    marginRight: "8px"
                  }}
                />
                <div
                  className="form_element_container"
                  style={{ height: "100%" }}
                  title={renderFieldsData.current[field.i]?.type}
                >
                  <FieldTag
                    key={field.id}
                    element={
                      fieldId
                        ? renderFieldsData.current[fieldId].components.find((f) => f.id === field.i)
                        : renderFieldsData.current[field.i]
                    }
                    setEditFieldId={setEditFieldId}
                    editFieldId={editFieldId}
                    dragFromOutside={dragFromOutside}
                    setDragFromOutside={setDragFromOutside}
                    fieldId={fieldId}
                    activeMode={activeMode}
                    from={from}
                  />
                </div>
              </div>
            ))
          ) : (
            <div>No field added</div>
          )}
        </GridLayout>
        <BuilderModal
          isOpen={fieldsRearrangeModal}
          onClose={() => setFieldsRearrangeModal(false)}
          modalName={"Edit Fields"}
        >
          <GroupFieldsRearrange fieldID={fieldsRearrangeModal} closeModal={() => reArrangeAndEditGroupField(false)} />
        </BuilderModal>
      </div>
    </>
  );
};

const GroupFieldsRearrange = ({ fieldID, closeModal }) => {
  const { renderFieldsData } = useFormContext();
  const { handleError } = useAxiosContext();
  const [formGridArray, setFormGridArray] = useState({
    desktop: [],
    mobile: []
  });
  const [fieldData, setFieldData] = useState({});
  const [selectedDevice, setSelectedDevice] = useState("desktop");
  const [{ width: elementWidth }, elementRef] = useElementDimensions();

  const saveArrangements = () => {
    const mobileDeviceData = formGridArray.mobile.reduce((acc, field) => {
      const obj = { [field.i]: {} };
      obj[field.i].grid = field;
      return {
        ...acc,
        ...obj
      };
    }, {});
    const desktopDeviceData = formGridArray.desktop.reduce((acc, field) => {
      const obj = { [field.i]: {} };
      obj[field.i].grid = field;
      return {
        ...acc,
        ...obj
      };
    }, {});

    const cardDesign = {
      mobile: mobileDeviceData,
      desktop: desktopDeviceData
    };

    renderFieldsData.current[fieldID].card_design = cardDesign;
    closeModal();
  };

  const handleOptionChange = (event) => {
    setSelectedDevice(event.target.value);
  };

  const handleLayoutChange = (list) => {
    setFormGridArray({ ...formGridArray, [selectedDevice]: list });
  };

  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        // const { data } = await axiosInstance.get(`/state-machine/${smId}`);
        // const globalDataFields = data?.global_data_form?.fields;

        const settingRenderArrayFromSectionData = (fields) => {
          const fieldData = {};
          const grid = fields.map((field, i) => {
            fieldData[field.id] = field;
            const gridObject = {
              w: 12,
              h: 1,
              x: 0,
              y: i,
              i: field.id
            };
            return gridObject;
          });
          return { grid, fieldData };
        };
        const groupFieldData = renderFieldsData.current[fieldID];

        if (!groupFieldData) {
          toastError(`Field with ID ${fieldID} not found.`);
          return;
        }

        const fieldsData = groupFieldData.components;
        const { grid, fieldData } = settingRenderArrayFromSectionData(fieldsData);
        let gridForMobile = [];
        let gridForDesktop = [];

        grid.forEach((g) => {
          if (groupFieldData.card_design?.mobile[g.i]) {
            gridForMobile.push(groupFieldData.card_design.mobile[g.i].grid);
          } else {
            gridForMobile.push(g);
          }
          if (groupFieldData.card_design?.desktop[g.i]) {
            gridForDesktop.push(groupFieldData.card_design.desktop[g.i].grid);
          } else {
            gridForDesktop.push(g);
          }
        });

        const deviceGridArray = {
          mobile: gridForMobile,
          desktop: gridForDesktop
        };

        setFormGridArray(deviceGridArray);
        setFieldData(fieldData);
      } catch (error) {
        handleError(error);
      }
    };
    fetchAndProcessData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={elementRef} style={{ width: "65vw", minHeight: "80vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "18px 0", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <label className="builder_checkbox_outer">
            <Input type="radio" value="desktop" checked={selectedDevice === "desktop"} onChange={handleOptionChange} />
            Desktop
          </label>
          <br />
          <label className="builder_checkbox_outer">
            <Input type="radio" value="mobile" checked={selectedDevice === "mobile"} onChange={handleOptionChange} />
            Mobile
          </label>
        </div>
        <Button onClick={saveArrangements}>Save</Button>
      </div>
      <GridLayout
        cols={12}
        rowHeight={60}
        width={elementWidth}
        layout={formGridArray[selectedDevice]}
        style={{ border: "2px dashed #e4e7ec" }}
        compactType={"vertical"}
        isDroppable={false}
        useCSSTransforms={true}
        onLayoutChange={handleLayoutChange}
      >
        {formGridArray[selectedDevice].length > 0 &&
          formGridArray[selectedDevice].map((item) => (
            <div key={item.i} className="single_field_render">
              {fieldData[item.i].label}
            </div>
          ))}
      </GridLayout>
    </div>
  );
};

export default FormGridLayout;
